import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Adds a variant to the cart and announces the update with Shopify's
 * CartLinesUpdateEvent so Horizon's cart drawer and bubble stay in sync.
 * Shared by the Interactive theme's ported sections.
 *
 * @param {string|number} variantId
 * @param {number} [quantity]
 * @param {string} [source]
 */
export async function ixAddToCart(variantId, quantity = 1, source = 'ix') {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const body = new FormData();
    body.append('id', String(variantId));
    body.append('quantity', String(quantity));
    const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    const result = await response.json();
    const cart = await (await fetch('/cart.js')).json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: Boolean(result.status),
        items: cart.items,
        itemCount: quantity,
        source,
      },
    });
    return result;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * <ix-quick-add> — wraps a button and adds `data-variant` to the cart on
 * click. Used by the brand stores cards.
 */
class IxQuickAdd extends HTMLElement {
  connectedCallback() {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('button'));
    if (!button) return;

    const resetFeedback = () => {
      delete button.dataset.added;
      delete button.dataset.loading;
    };
    // stale tick/loading state can survive bfcache restores — always reset
    window.addEventListener('pageshow', resetFeedback);

    button.addEventListener('click', async (event) => {
      event.preventDefault();
      if (!this.dataset.variant || button.disabled || button.dataset.loading) return;
      // Horizon-native feedback: play the checkmark-burst tick right away
      // (markup permitting) while the add runs. The reset timers are armed
      // up front so the button can never stay stuck in a feedback state.
      button.dataset.loading = 'true';
      if (button.querySelector('.add-to-cart__added')) {
        button.dataset.added = 'true';
        setTimeout(() => {
          delete button.dataset.added;
        }, 1600);
      }
      const loadingSafety = setTimeout(() => {
        delete button.dataset.loading;
      }, 8000);
      try {
        await ixAddToCart(this.dataset.variant, 1, 'ix-quick-add');
      } finally {
        clearTimeout(loadingSafety);
        delete button.dataset.loading;
      }
    });
  }
}

if (!customElements.get('ix-quick-add')) {
  customElements.define('ix-quick-add', IxQuickAdd);
}
